import React from 'react';
import T from './theme';
import { formatPace } from './utils';
import TokensPanel from './TokensPanel';

export default function ConfigModal({
  goalEvent, setGoalEvent,
  trainingAppetite, setTrainingAppetite,
  trainingBands,
  basePaceInput, onBasePaceChange,
  ageInput, setAgeInput,
  hrMaxInput, setHrMaxInput,
  hrRestInput, setHrRestInput,
  lthrInput, setLthrInput,
  vo2Input, setVo2Input,
  massKg, setMassKg,
  gutTolerance, setGutTolerance,
  lossRate, setLossRate,
  withingsConnected,
  onConnectWithings,
  onSave, onClose,
  api,
}) {
  return (
    <div
      role="dialog"
      aria-label="Settings"
      onClick={onClose}
      style={T.modalOverlayStyle}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={T.modalBoxStyle}
      >
        <h2 style={{ marginTop: 0 }}>Settings</h2>
        <label style={T.labelStyle}>
          Distance goal
          <select
            value={goalEvent}
            onChange={(e) => setGoalEvent(e.target.value)}
            style={T.inputStyle}
          >
            <option value="general">General fitness</option>
            <option value="5k">5K</option>
            <option value="10k">10K</option>
            <option value="half">Half marathon</option>
            <option value="marathon">Marathon</option>
            <option value="50k">50K</option>
            <option value="50mi">50 miles</option>
            <option value="100mi">100 miles</option>
          </select>
          <span style={{ display: 'block', color: T.color.textLight, fontSize: T.font.sm, marginTop: 6 }}>
            Sets weekly volume and long-run length for the next workout. Not a day-by-day plan.
          </span>
        </label>
        <label style={T.labelStyle}>
          Training appetite
          <select
            value={trainingAppetite || ''}
            onChange={(e) => setTrainingAppetite(e.target.value)}
            style={T.inputStyle}
            aria-label="training appetite"
          >
            <option value="">From goal (50mi/100mi → Dedicated)</option>
            <option value="standard">Standard</option>
            <option value="dedicated">Dedicated</option>
          </select>
          <span style={{ display: 'block', color: T.color.textLight, fontSize: T.font.sm, marginTop: 6 }}>
            Dedicated is the high time-on-feet habit: real longs, leftover-tolerant Recommended.
            Standard recovers first when leftover is high. Protect still rests either way.
          </span>
        </label>
        {trainingBands && ['easy_runs', 'all_runs', 'recent_runs', 'gap_hr'].includes(trainingBands.easy_pace_source) ? (
          <p style={{ margin: '12px 0' }}>
            Estimated easy pace{' '}
            <strong>{trainingBands.easy_pace_display || formatPace(trainingBands.easy_pace)}</strong>
            {trainingBands.easy_pace_n
              ? ` from ${trainingBands.easy_pace_n} ${
                  trainingBands.easy_pace_source === 'easy_runs' ? 'easy' : 'recent'
                } runs`
              : ''}
            . Your training zones and weather-adjusted paces are based on this.
          </p>
        ) : (
          <label style={T.labelStyle}>
            Easy pace (min/mile)
            <input
              type="text"
              value={basePaceInput}
              onChange={onBasePaceChange}
              placeholder="mm:ss (e.g., 8:30)"
              style={T.inputStyle}
            />
          </label>
        )}
        <label style={T.labelStyle}>
          Age
          <input
            type="number"
            min="12"
            max="100"
            value={ageInput}
            onChange={(e) => setAgeInput(e.target.value)}
            placeholder="used to estimate max HR if not set"
            style={T.inputStyle}
          />
        </label>
        <label style={T.labelStyle}>
          Max HR
          <input
            type="number"
            min="120"
            max="220"
            value={hrMaxInput}
            onChange={(e) => setHrMaxInput(e.target.value)}
            placeholder="optional"
            style={T.inputStyle}
          />
        </label>
        <label style={T.labelStyle}>
          Resting HR
          <input
            type="number"
            min="30"
            max="100"
            value={hrRestInput}
            onChange={(e) => setHrRestInput(e.target.value)}
            placeholder="optional"
            style={T.inputStyle}
          />
        </label>
        <label style={T.labelStyle}>
          Threshold HR (LTHR)
          <input
            type="number"
            min="110"
            max="200"
            value={lthrInput}
            onChange={(e) => setLthrInput(e.target.value)}
            placeholder="bpm you get gassed near, e.g. 150"
            style={T.inputStyle}
          />
        </label>
        <label style={T.labelStyle}>
          Garmin VO2 max
          <input
            type="number"
            min="20"
            max="90"
            step="0.1"
            value={vo2Input}
            onChange={(e) => setVo2Input(e.target.value)}
            placeholder="ml/kg/min"
            style={T.inputStyle}
          />
        </label>
        <label style={T.labelStyle}>
          Body mass (kg)
          <input
            type="number"
            min="35"
            max="180"
            step="0.5"
            value={massKg}
            onChange={(e) => setMassKg(e.target.value)}
            style={T.inputStyle}
          />
        </label>
        <p style={{ margin: '8px 0 12px' }}>
          {withingsConnected ? (
            <span style={{ color: T.color.textMuted }}>Withings linked. The scale pushes a weigh-in here, and UMAX also pulls at least once a day.</span>
          ) : (
            <>
              <button type="button" onClick={onConnectWithings}>
                Connect Withings
              </button>
              <span style={{ display: 'block', color: T.color.textLight, fontSize: T.font.sm, marginTop: 6 }}>
                Links this UMAX account to your Withings profile. Other people on the same scale connect from their own login.
              </span>
            </>
          )}
        </p>
        <label style={T.labelStyle}>
          Gut tolerance
          <select
            value={gutTolerance}
            onChange={(e) => setGutTolerance(e.target.value)}
            style={T.inputStyle}
          >
            <option value="low">Low</option>
            <option value="med">Medium</option>
            <option value="high">High</option>
          </select>
        </label>
        {setLossRate && (
          <label style={T.labelStyle}>
            Weight loss rate
            <select
              value={String(lossRate ?? 0)}
              onChange={(e) => setLossRate(Number(e.target.value))}
              style={T.inputStyle}
              aria-label="weight loss rate"
            >
              <option value="0">Maintain</option>
              <option value="0.25">0.25 lb / week</option>
              <option value="0.5">0.5 lb / week</option>
              <option value="0.75">0.75 lb / week</option>
              <option value="1">1 lb / week</option>
            </select>
            <span style={{ display: 'block', color: T.color.textLight, fontSize: T.font.sm, marginTop: 6 }}>
              Sets daily nutrition targets. Long runs and workouts are protected: no deficit the day
              before or the day of, half the day after.
            </span>
          </label>
        )}
        <p style={{ color: T.color.textLight, fontSize: T.font.sm }}>
          Zones are based on percent of threshold HR (same idea as Garmin).
          Leave threshold blank to estimate from your recent runs.
        </p>
        {api && <TokensPanel api={api} />}
        <button type="button" onClick={onSave}>
          Save
        </button>
        {' '}
        <button type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
